import { InvestigatorAveragesService } from '../../../services/investigator-averages.service';
import { InvestigatorForecastService } from '../../../services/investigator-forecast.service';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { InvestigatorForecastForm } from '../../../investigator-forecast.types';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { decimalDifference, decimalRoundingToNumber } from '@utils/floating-math';

@UntilDestroy()
@Component({
  selector: 'aux-investigator-forecast-overview-averages',
  templateUrl: './investigator-forecast-overview-averages.component.html',
  styles: [
    `
      ::ng-deep .averages-form input:not([type='radio']) {
        color: black;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestigatorForecastOverviewAveragesComponent implements OnInit {
  @Input() form!: FormGroup<InvestigatorForecastForm>;

  private maxPercent = 100;

  averagesValues$ = this.investigatorForecastService.averagesValues$;

  editMode$ = this.investigatorAveragesService.editMode$;

  formFieldLabels$ = this.averagesValues$.pipe(
    map((values) => {
      const { current_average_patient_cost, total_forecasted_patients } =
        values.find(({ last_updated }) => last_updated === 'Current') || {};

      return {
        totalForecastedPatients: total_forecasted_patients,
        averagePatientCostPerSite: current_average_patient_cost,
      };
    })
  );

  constructor(
    private investigatorForecastService: InvestigatorForecastService,
    private investigatorAveragesService: InvestigatorAveragesService
  ) {}

  ngOnInit(): void {
    this.recalculateAverageForm();
    this.roundInvoiceablesField('otherInvoiceablesPerc', 'overheadInvoiceablesPerc');
    this.roundInvoiceablesField('overheadInvoiceablesPerc', 'otherInvoiceablesPerc');
    this.updateAutoPopulateFieldsOnSiteChange();
  }

  getSiteContract(contractedSitesDate: string) {
    return (
      this.averagesValues$
        .getValue()
        .find(({ last_updated }) => last_updated === contractedSitesDate)?.site_count || 0
    );
  }

  private updateAutoPopulateFieldsOnSiteChange() {
    this.form
      .get(['averages', 'contractedSites'])
      ?.valueChanges.pipe(
        untilDestroyed(this),
        tap((contractedSiteDate) => {
          const averages = this.averagesValues$
            .getValue()
            .find(({ last_updated }) => last_updated === contractedSiteDate);

          if (averages) {
            const { current_average_patient_cost, average_patient_cost } = averages;

            const averagePatientCost =
              averages.last_updated === 'Current'
                ? current_average_patient_cost
                : average_patient_cost;

            this.form.get('averages')?.patchValue({
              totalForecastedPatients: averages.total_forecasted_patients,
              averagePatientCost: decimalRoundingToNumber(averagePatientCost),
            });
          }
        })
      )
      .subscribe();
  }

  private roundInvoiceablesField(formControlName: string, dependingControlName: string) {
    const formControl = this.form.get(['averages', formControlName]);
    const dependingControl = this.form.get(['averages', dependingControlName]);

    if (formControl) {
      formControl.valueChanges
        .pipe(
          untilDestroyed(this),
          distinctUntilChanged(isEqual),
          tap((percentage: number) => {
            const newVal = decimalDifference(this.maxPercent, percentage, 2);

            dependingControl?.patchValue(newVal, { emitEvent: false });
          })
        )
        .subscribe();
    }
  }

  private recalculateAverageForm() {
    const formGroup = this.form.get('averages');

    formGroup?.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(isEqual),
        tap((values) => {
          if (this.investigatorForecastService.isLoadingSettings$.getValue()) {
            return;
          }

          const forecastedVisitCosts = this.investigatorAveragesService.getForecastedVisitCost(
            values.totalForecastedPatients,
            values.averagePatientCost
          );

          const forecastedInvoiceables = this.investigatorAveragesService.getForecastedInvoiceables(
            forecastedVisitCosts,
            values.invoiceables
          );

          const totalInvestigatorCosts = this.investigatorAveragesService.getTotalInvestigatorCost(
            forecastedVisitCosts,
            forecastedInvoiceables
          );

          const remainingAmount = this.investigatorAveragesService.getRemainingAmount(
            totalInvestigatorCosts,
            this.form.get('contracted')?.value.spendToDateAmount || 0
          );

          formGroup.patchValue(
            {
              forecastedVisitCosts,
              forecastedInvoiceables,
              totalInvestigatorCosts,
              remainingAmount,
            },
            { emitEvent: false }
          );
        })
      )
      .subscribe();
  }
}
